/**
 * testCatalogHelp.tsx — page-guide definition for Admin → Test Catalogue.
 *
 * DATA file (CODING-STANDARDS column-config pattern): the structure is a
 * plain HelpContent object; JSX appears only for the live example chips,
 * which reuse the page's REAL components so users see exactly what the page
 * shows. Rendered by PageHelpDialog; shape-checked by
 * src/__tests__/pageHelpContent.test.ts.
 */

import { DefinitionStatusChips } from '../../../pages/admin/testCatalog/TestDefinitionListColumn';
import { ROUTES } from '../../../constants/routes';
import type { HelpContent } from '../helpTypes';

/** Every status chip the list can show, rendered by the page's own component. */
const allStatusChipsExample = (
  <DefinitionStatusChips
    flags={{
      noRecipe: true,
      needsMachineTime: true,
      unconfirmedMapping: true,
      unmapped: true,
      inactive: true,
    }}
  />
);

export const testCatalogHelp: HelpContent = {
  pageKey: 'test-catalog',
  title: 'Test Catalogue — page guide',
  heroSrc: '/help/img/catalog-hero.webp',
  heroAlt: 'Illustration of the test catalogue: one card with a price side and a scheduling side',
  overview: [
    'The Test Catalogue is the master list of every test the lab offers. Each entry is one '
    + 'object with two sides: a commercial side (price, ASTM and BST codes, description, '
    + 'assumptions) that quotes use, and an operational side (machine time in minutes, run '
    + 'mode, required skill, capable machines) that the schedule uses.',
    'Pick a test on the left and edit everything about it on the right — then press Save '
    + 'once. Both sides are written together, and renaming a test renames its scheduling '
    + 'task type in lockstep, so the two never drift apart.',
    'Tests with problems rise to the top of the list wearing amber or red chips, so this '
    + 'page doubles as a to-do list: anything flagged here can hold up quoting or scheduling.',
  ],
  snapshot: {
    intro: 'This sketch mirrors the page. Click a numbered dot (or an entry on the right) '
      + 'to see what that part does.',
    areas: [
      { id: 'header', sketch: 'titleBar', rect: { left: 1.5, top: 2, width: 54, height: 9 }, marker: { left: 4.5, top: 6.5 } },
      { id: 'toolbar', sketch: 'toolbar', rect: { left: 57, top: 2, width: 41.5, height: 9 }, marker: { left: 60, top: 6.5 } },
      { id: 'list', sketch: 'listColumn', rect: { left: 1.5, top: 13.5, width: 32, height: 84.5 }, marker: { left: 4.5, top: 18.5 } },
      { id: 'form', sketch: 'formPane', rect: { left: 35.5, top: 13.5, width: 63, height: 71 }, marker: { left: 38.5, top: 18.5 } },
      { id: 'saveBar', sketch: 'footerBar', rect: { left: 35.5, top: 86.5, width: 63, height: 11.5 }, marker: { left: 38.5, top: 92 } },
    ],
    items: [
      {
        id: 'header',
        title: 'Title and count',
        points: [
          'Shows how many active tests are in the catalogue right now.',
          'Retired tests are not counted — they sit behind the Inactive filter in the list below.',
        ],
      },
      {
        id: 'toolbar',
        title: 'Toolbar',
        points: [
          'Prerequisites — set which tests must finish before another can start.',
          'Methodology — the step-by-step method write-ups attached to tests.',
          'Skills — manage the skill list technicians are rated on (opens a side panel).',
          'Operational steps — the three auto-inserted rock prep steps (Cutting, Grinding, '
          + 'Conformity) live here, not in the main list.',
          'Import CSV adds many tests at once; Add Test creates a single new one.',
        ],
      },
      {
        id: 'list',
        title: 'The test list',
        points: [
          'Search by name, ASTM or BST code, or category; narrow further with the filter chips.',
          'Tests that need attention sort to the top automatically.',
          'No recipe (red) — the test has no scheduling step at all: it can be quoted, but it '
          + 'never generates schedule tasks.',
          'Needs machine·time (amber) — no machine minutes or no capable machine yet, so the '
          + 'scheduler cannot place it well. Fix it under Machines & Time.',
          'Unmapped (amber) — no lab test points at this entry, so work-order lines cannot reach it.',
          'Unconfirmed (amber outline) — the lab mapping is an estimate. It still schedules; '
          + 'confirm it when the lab verifies.',
          'Inactive (grey) — retired, kept for history.',
        ],
        example: allStatusChipsExample,
        exampleCaption: 'The chips exactly as they appear on a row:',
      },
      {
        id: 'form',
        title: 'The one form',
        points: [
          'One form covers the whole test, in sections: Identity, Pricing, Scheduling, Text, '
          + 'Links, and an Advanced multi-step recipe.',
          'Pricing, description, and assumptions feed quotes.',
          'Scheduling holds the run mode (Attended, or Continuous 24/7 for tests that run '
          + 'through nights and weekends), machine minutes, the required skill, and Machines '
          + '& Time — which machines can run the test and how long each takes. This is what '
          + 'the scenario planner works from.',
          'Links shows which lab test maps to this entry and jumps to the Test Mapping page.',
          'Most tests are a single step; multi-step recipes live under Advanced.',
        ],
      },
      {
        id: 'saveBar',
        title: 'Save, Discard, Delete',
        points: [
          'One Save writes both sides of the test together — commercial and scheduling.',
          'Renaming the test renames its scheduling task type with it.',
          'Creating a test also creates its matching task type (a switch controls this), so '
          + 'new tests are schedulable from day one.',
          'Delete checks the scheduling impact first and tells you before asking to confirm.',
        ],
      },
    ],
  },
  connections: {
    imageSrc: '/help/img/connections-hero.webp',
    intro: 'A catalogue test sits in the middle of the chain. On one side, lab tests reach it '
      + 'through Test Mapping. On the other, everything it carries — minutes, run mode, skill, '
      + 'machines, price — feeds the pages that plan and quote the work. Click any chip with '
      + 'an arrow icon to go there.',
    stages: [
      {
        title: 'The lab side',
        nodes: [
          { id: 'labTest', label: 'Lab test', sublabel: 'what engineers pick on a work order', route: ROUTES.TESTS, tone: 'neutral' },
        ],
      },
      {
        title: 'The link',
        nodes: [
          { id: 'mapping', label: 'Test Mapping', sublabel: 'connects each lab test to one catalogue test', route: ROUTES.ADMIN_TEST_MAPPING, tone: 'sage' },
        ],
      },
      {
        title: 'This page',
        nodes: [
          { id: 'catalog', label: 'Catalogue test', sublabel: 'one object: price + scheduling', route: ROUTES.ADMIN_TEST_CATALOG, tone: 'sage' },
        ],
      },
      {
        title: 'What it carries',
        nodes: [
          { id: 'machineTime', label: 'Machine time & run mode', sublabel: 'minutes; attended or 24/7', tone: 'machine' },
          { id: 'machines', label: 'Capable machines', sublabel: 'which machines can run it', route: ROUTES.SCHEDULE_MACHINES, tone: 'machine' },
          { id: 'skill', label: 'Required skill', sublabel: 'who is qualified to run it', route: ROUTES.SCHEDULE_SKILL_MATRIX, tone: 'skill' },
          { id: 'price', label: 'Price & assumptions', sublabel: 'the commercial side', tone: 'quote' },
        ],
      },
      {
        title: 'Who uses it',
        nodes: [
          { id: 'scenario', label: 'Scenario Planner', sublabel: 'plans machines and people', route: ROUTES.SCHEDULE_SCENARIO, tone: 'sage' },
          { id: 'quotes', label: 'Quotes', sublabel: 'prices the work', route: ROUTES.QUOTES, tone: 'quote' },
        ],
      },
    ],
    footnote: 'If a lab test has no mapping, its work-order lines silently cannot be scheduled '
      + '— Scenario Planning counts them as “not schedulable”. Fix that on the Test Mapping page.',
  },
};
